import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import '../nativeitems/NativeItems.css';

// Documents Outbox — the human review step for the draft-first email flow
// (task #66). The Email button on a native record page POSTs to
// /api/documents/<type>/<id>/queue, which writes a draft here as
// pending_approval WITHOUT sending. This page lists those drafts and lets a
// reviewer approve them; approval is what releases a document to send.
//
//   GET  /api/documents/outbox?state=pending_approval   list drafts
//   POST /api/documents/outbox/:id/approve              approve one
//
// Rendered defensively — the queue/outbox endpoints are landing alongside this
// UI (sibling agent), so unknown/absent fields degrade gracefully and a 404
// simply shows "no drafts / not enabled yet" rather than blowing up.

const fmtStamp = (d) => (d ? new Date(d).toLocaleString() : '—');

function docLabel(row) {
  return row.type || row.document_type || row.doc_type || 'document';
}
function docRecordId(row) {
  return row.record_id || row.recordId || row.id_ref || row.reference_id || null;
}
function recipient(row) {
  return row.to || row.recipient || row.recipient_email || row.to_email || null;
}

export default function DocumentOutbox() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get('/api/documents/outbox', { params: { state: 'pending_approval' } });
      // Accept a few plausible envelope shapes from the sibling endpoint.
      const list = Array.isArray(data) ? data : (data.outbox || data.documents || data.items || data.rows || []);
      setRows(list);
      setNote(data && !Array.isArray(data) ? data.note || null : null);
    } catch (err) {
      // 404 = endpoint not merged yet; treat as "empty, not enabled" not a crash.
      if (err.response?.status === 404) {
        setRows([]);
        setNote('The outbox endpoint is not available yet. Queued documents will appear here once it is live.');
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (row) => {
    if (!window.confirm('Approve this document? Approving releases it from the draft/pending state.')) return;
    setApprovingId(row.id);
    try {
      await axios.post(`/api/documents/outbox/${encodeURIComponent(row.id)}/approve`);
      await load();
    } catch (err) {
      window.alert(err.response?.data?.error || err.message);
    } finally {
      setApprovingId(null);
    }
  };

  return (
    <div className="nit-page">
      <button className="nit-btn nit-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>

      <div className="nit-item-head" style={{ marginTop: 8 }}>
        <h1><Mail size={20} style={{ verticalAlign: '-3px', marginRight: 8 }} />Documents Outbox</h1>
        <span className="nit-tag">pending approval</span>
      </div>

      <p className="nit-caption" style={{ marginTop: 4 }}>
        Documents queued from a record&apos;s <strong>Email…</strong> action land here as drafts.
        Nothing is sent until a reviewer approves it below.
      </p>

      <div className="nit-card">
        <div className="nit-hist-head">
          <h3>Drafts awaiting approval</h3>
          <button className="nit-btn" onClick={load} disabled={loading}>Refresh</button>
        </div>

        {loading && <div className="nit-loading">Loading outbox…</div>}
        {error && <div className="nit-error">{error}</div>}

        {!loading && !error && rows.length === 0 && (
          <div className="nit-empty">
            No documents are waiting for approval.
            <br />
            {note || 'Queue one from a Sales Order or Purchase Order using its Email… button.'}
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className="nit-table-wrap">
            <table className="nit-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Record</th>
                  <th>Recipient</th>
                  <th>Queued</th>
                  <th>State</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td><code>{docLabel(row)}</code></td>
                    <td className="nit-muted">{docRecordId(row) || '—'}</td>
                    <td className="nit-muted">{recipient(row) || '—'}</td>
                    <td className="nit-muted">{fmtStamp(row.created_at || row.queued_at)}</td>
                    <td><span className="nit-tag">{row.state || 'pending_approval'}</span></td>
                    <td className="r">
                      <button
                        className="nit-btn nit-btn-primary"
                        disabled={approvingId === row.id}
                        onClick={() => approve(row)}
                      >
                        <CheckCircle size={13} /> {approvingId === row.id ? 'Approving…' : 'Approve'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
